/**
 * IssueDrawer -- right slide-in detail panel for viewing a selected issue.
 *
 * Renders inside the Tasks panel (not a separate modal).
 * Shows issue header, location, details, impact, notes, audit trail,
 * and state-dependent action buttons in the footer.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { Issue, IssueState } from '@/types/tasks';
import { SEVERITY_COLORS, STATE_COLORS } from '@/types/tasks';
import Button from '@/components/common/Button';
import ThermalAnnotator from '@/components/editor/thermal/ThermalAnnotator';
import { resolveImageSrc } from '@/utils/photoBlobs';
import { useIsReadOnlyViewer } from '@/hooks/useShareViewer';
import { useProjectStore } from '@/stores';
import './IssueDrawer.css';

// ─── Props ───────────────────────────────────────────────────────────────────

interface IssueDrawerProps {
  issue: Issue;
  onClose: () => void;
  onCreateTask: () => void;
  onAcknowledge: () => void;
  onZoomToMap: () => void;
  onSuppress: () => void;
  onReopen: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatNumber(n: number, decimals = 1): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SeverityBadge({ severity, color }: { severity: string; color: string }) {
  return (
    <span
      className="tp-drawer-severity-badge"
      style={{ background: color, color: '#fff' }}
    >
      {severity}
    </span>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="tp-drawer-field">
      <span className="tp-drawer-field__label">{label}</span>
      <span className="tp-drawer-field__value">{value}</span>
    </div>
  );
}

function AssetPathBreadcrumb({ assetPath }: { assetPath: Issue['assetPath'] }) {
  const parts: string[] = [];
  if (assetPath.station) parts.push(`Station: ${assetPath.station}`);
  if (assetPath.inverter) parts.push(`Inverter: ${assetPath.inverter}`);
  if (assetPath.mppt) parts.push(`MPPT: ${assetPath.mppt}`);
  if (assetPath.dccb) parts.push(`DCCB: ${assetPath.dccb}`);
  if (assetPath.string) parts.push(`String: ${assetPath.string}`);
  if (assetPath.module) parts.push(`Module: ${assetPath.module}`);

  if (parts.length === 0) return <span className="tp-drawer-field__value">--</span>;

  return (
    <div className="tp-drawer-asset-path">
      {parts.map((part, i) => (
        <span key={i} className="tp-drawer-asset-path__segment">
          {i > 0 && <span className="tp-drawer-asset-path__sep">&rsaquo;</span>}
          <span>{part}</span>
        </span>
      ))}
    </div>
  );
}

// ─── Footer action buttons by state ──────────────────────────────────────────

function FooterActions({
  state,
  onCreateTask,
  onAcknowledge,
  onZoomToMap,
  onSuppress,
  onReopen,
}: {
  state: IssueState;
  onCreateTask: () => void;
  onAcknowledge: () => void;
  onZoomToMap: () => void;
  onSuppress: () => void;
  onReopen: () => void;
}) {
  // F-02E-2B: share viewers only get "Zoom to Map" (pure view control).
  // Create Task / Acknowledge / Suppress / Reopen all mutate (open
  // CreateTaskModal or call task-store state transitions that fire
  // backend writes through the entity API).
  const isReadOnly = useIsReadOnlyViewer();
  if (isReadOnly) {
    return (
      <Button variant="ghost" size="sm" onClick={onZoomToMap} data-testid="issue-drawer-zoom-readonly">
        Zoom to Map
      </Button>
    );
  }
  switch (state) {
    case 'Open':
      return (
        <>
          <Button variant="primary" size="sm" onClick={onCreateTask}>
            Create Task
          </Button>
          <Button variant="warning" size="sm" onClick={onAcknowledge}>
            Acknowledge
          </Button>
          <Button variant="ghost" size="sm" onClick={onZoomToMap}>
            Zoom to Map
          </Button>
          <Button variant="danger" size="sm" onClick={onSuppress}>
            Suppress
          </Button>
        </>
      );
    case 'Acknowledged':
      return (
        <>
          <Button variant="primary" size="sm" onClick={onCreateTask}>
            Create Task
          </Button>
          <Button variant="ghost" size="sm" onClick={onZoomToMap}>
            Zoom to Map
          </Button>
          <Button variant="danger" size="sm" onClick={onSuppress}>
            Suppress
          </Button>
        </>
      );
    case 'ConvertedToTask':
      return (
        <Button variant="ghost" size="sm" onClick={onZoomToMap}>
          Zoom to Map
        </Button>
      );
    case 'Closed':
      return (
        <>
          <Button variant="secondary" size="sm" onClick={onReopen}>
            Reopen
          </Button>
          <Button variant="ghost" size="sm" onClick={onZoomToMap}>
            Zoom to Map
          </Button>
        </>
      );
    case 'Suppressed':
      return (
        <>
          <Button variant="secondary" size="sm" onClick={onReopen}>
            Reopen
          </Button>
          <Button variant="ghost" size="sm" onClick={onZoomToMap}>
            Zoom to Map
          </Button>
        </>
      );
    default:
      return null;
  }
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function IssueDrawer({
  issue,
  onClose,
  onCreateTask,
  onAcknowledge,
  onZoomToMap,
  onSuppress,
  onReopen,
}: IssueDrawerProps) {
  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const severityColor = SEVERITY_COLORS[issue.severity];
  const stateColor = STATE_COLORS[issue.state];

  // Thermal image preview
  const thermalImgRef = useRef<HTMLImageElement | null>(null);
  const noop = useCallback(() => {}, []);
  // A child fault (PFC-4B) has no image of its own — resolve its SOURCE photo's
  // image via sourcePhotoId for display only (read-only; never copied).
  const ownImage = issue.image || '';
  const sourcePhotoImage =
    !ownImage && issue.sourcePhotoId
      ? useProjectStore.getState().data.indications.find((i) => i.id === issue.sourcePhotoId)?.properties.image || ''
      : '';
  const imageSrc = ownImage || sourcePhotoImage ? resolveImageSrc(ownImage || sourcePhotoImage) : '';
  const thermalData = issue.thermalData;
  const hasThermalOverlay = thermalData &&
    ((thermalData.rois && thermalData.rois.length > 0) ||
      (thermalData.points && thermalData.points.length > 0));

  return (
    <div className="tp-drawer-panel open" role="complementary" aria-label="Issue details">
      {/* ── Header ── */}
      <div className="tp-drawer-header">
        <div className="tp-drawer-header__top">
          <div className="tp-drawer-header__titles">
            <span className="tp-drawer-header__category">{issue.indType}</span>
            <SeverityBadge severity={issue.severity} color={severityColor} />
          </div>
          <button
            type="button"
            className="tp-drawer-close"
            onClick={onClose}
            aria-label="Close issue drawer"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="tp-drawer-header__meta">
          <span className="tp-drawer-header__id">{issue.issueId}</span>
          <span
            className="tp-drawer-state-badge"
            style={{ background: stateColor, color: '#fff' }}
          >
            {issue.state}
          </span>
        </div>
      </div>

      {/* ── Body (scrollable) ── */}
      <div className="tp-drawer-body">

        {/* Thermal image / photo preview */}
        {imageSrc && (
          <div className="tp-drawer-image-preview">
            {hasThermalOverlay ? (
              <div className="tp-drawer-thermal-wrap">
                <ThermalAnnotator
                  imageSrc={imageSrc}
                  rois={thermalData.rois || []}
                  points={thermalData.points || []}
                  activeRoiIndex={-1}
                  onPointerDown={noop}
                  onPointerMove={noop}
                  onPointerUp={noop}
                  imgRef={thermalImgRef}
                  interactive={false}
                />
              </div>
            ) : (
              <img
                className="tp-drawer-thumb"
                src={imageSrc}
                alt=""
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            {thermalData?.rois?.some((r) => r.stats && r.stats.delta_t > 0) && (
              <span className="tp-drawer-delta-tag">
                {thermalData.rois
                  .filter((r) => r.stats && r.stats.delta_t > 0)
                  .map((r, i) => `\u0394T${thermalData.rois.length > 1 ? (i + 1) : ''} ${r.stats!.delta_t.toFixed(1)}\u00B0C`)
                  .join(' \u00B7 ')}
              </span>
            )}
          </div>
        )}

        {/* Location */}
        <section className="tp-drawer-section">
          <h3 className="tp-drawer-section__title">Location</h3>
          <AssetPathBreadcrumb assetPath={issue.assetPath} />
        </section>

        {/* Details */}
        <section className="tp-drawer-section">
          <h3 className="tp-drawer-section__title">Details</h3>
          <FieldRow label="Type" value={issue.indType} />
          <FieldRow label="Confidence" value={`${issue.confidence}%`} />
          <FieldRow label="Detected" value={formatDate(issue.detectedAt)} />
          <FieldRow label="Last seen" value={formatDate(issue.lastSeenAt)} />
        </section>

        {/* Recommended Action */}
        <section className="tp-drawer-section">
          <h3 className="tp-drawer-section__title">Recommended Action</h3>
          <p className="tp-drawer-action-text">{issue.recommendedAction}</p>
        </section>

        {/* Advanced Alarm Details */}
        {issue.advancedAlarmData && (() => {
          const ad = issue.advancedAlarmData;
          const d = ad.details;
          const isExpr = ad.ruleType === 'expression';
          const hasPipe = d.includes(' | ');
          const hasColon = d.includes(':');

          let conditionText = '';
          let variables: { name: string; value: string }[] = [];
          let conditions: { expr: string; result: string }[] = [];

          if (isExpr && hasPipe) {
            const [varPart, condPart] = d.split(' | ');
            variables = varPart.split(', ').map((v) => {
              const [name, ...rest] = v.split('=');
              return { name: name.trim(), value: rest.join('=').trim() };
            });
            const combinator = condPart.includes(' AND ') ? ' AND ' : ' OR ';
            conditions = condPart.split(combinator).map((c) => {
              const arrow = c.lastIndexOf(' → ');
              if (arrow >= 0) {
                return { expr: c.slice(0, arrow).trim(), result: c.slice(arrow + 3).trim() };
              }
              return { expr: c.trim(), result: '' };
            });
          } else if (hasColon) {
            conditionText = d.split(':')[0].trim();
          } else {
            conditionText = d;
          }

          return (
            <section className="tp-drawer-section">
              <h3 className="tp-drawer-section__title">Alarm Details</h3>

              <FieldRow label="Rule" value={ad.ruleName} />
              <FieldRow
                label="Type"
                value={ad.ruleType === 'zero_check' ? 'Zero Check' : ad.ruleType === 'deviation' ? 'Deviation' : 'Expression'}
              />

              {conditionText && (
                <div className="tp-drawer-alarm-condition">{conditionText}</div>
              )}

              {variables.length > 0 && (
                <div className="tp-drawer-alarm-table-wrap">
                  <span className="tp-drawer-alarm-table-label">Variables</span>
                  <table className="tp-drawer-alarm-table">
                    <thead><tr><th>Var</th><th>Value</th></tr></thead>
                    <tbody>
                      {variables.map((v, i) => (
                        <tr key={i}><td>{v.name}</td><td>{v.value}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {conditions.length > 0 && (
                <div className="tp-drawer-alarm-table-wrap">
                  <span className="tp-drawer-alarm-table-label">Conditions</span>
                  <table className="tp-drawer-alarm-table">
                    <thead><tr><th>Expression</th><th>Result</th></tr></thead>
                    <tbody>
                      {conditions.map((c, i) => (
                        <tr key={i}>
                          <td>{c.expr}</td>
                          <td style={{ color: c.result === 'true' ? '#16a34a' : undefined, fontWeight: c.result === 'true' ? 600 : undefined }}>
                            {c.result}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {ad.matchedMetrics && ad.matchedMetrics.length > 0 && (
                <div className="tp-drawer-alarm-table-wrap">
                  <span className="tp-drawer-alarm-table-label">
                    Matched Metrics ({ad.matchedMetrics.length})
                  </span>
                  <table className="tp-drawer-alarm-table">
                    <thead><tr><th>Entity</th><th>Metric</th><th>Value</th></tr></thead>
                    <tbody>
                      {ad.matchedMetrics.map((m, i) => (
                        <tr key={i}>
                          <td>{m.entity_id}</td>
                          <td>{m.metric}</td>
                          <td>{m.value ?? 'null'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })()}

        {/* Impact */}
        <section className="tp-drawer-section">
          <h3 className="tp-drawer-section__title">Impact</h3>
          <FieldRow
            label="Power loss"
            value={`${formatNumber(issue.estimatedImpact.powerLossW, 0)} W`}
          />
          <FieldRow
            label="Energy loss"
            value={`${formatNumber(issue.estimatedImpact.energyLossKWh, 1)} kWh/yr`}
          />
          <FieldRow
            label="Revenue loss"
            value={`$${formatNumber(issue.estimatedImpact.revenueLoss, 2)}/yr`}
          />
        </section>

        {/* Notes */}
        {issue.notes && (
          <section className="tp-drawer-section">
            <h3 className="tp-drawer-section__title">Notes</h3>
            <p className="tp-drawer-notes-text">{issue.notes}</p>
          </section>
        )}

        {/* Audit trail */}
        {issue.auditLog.length > 0 && (
          <section className="tp-drawer-section">
            <h3 className="tp-drawer-section__title">Audit Trail</h3>
            <ul className="tp-drawer-audit-list">
              {issue.auditLog.map((entry, i) => (
                <li key={i} className="tp-drawer-audit-entry">
                  <span className="tp-drawer-audit-entry__time">
                    {formatTimestamp(entry.timestamp)}
                  </span>
                  <span className="tp-drawer-audit-entry__action">
                    {entry.action}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* ── Footer actions ── */}
      <div className="tp-drawer-footer">
        <FooterActions
          state={issue.state}
          onCreateTask={onCreateTask}
          onAcknowledge={onAcknowledge}
          onZoomToMap={onZoomToMap}
          onSuppress={onSuppress}
          onReopen={onReopen}
        />
      </div>
    </div>
  );
}
